import type { NL, NLGeneric } from './nlMain';
import type { TranslationKey } from './types';
import { en } from './enMain';
import { nl } from './nlMain';

const languageFiles = { en, nl } as const;

export type LanguageCode = keyof typeof languageFiles;
export const LanguageCodes = Object.keys(languageFiles) as LanguageCode[];

export type DefaultLanguage = NLGeneric;
export type DefaultLanguageKeys = keyof NL;

type NestedObject = {
    [key: string]: string | NestedObject;
};

export function isValidLanguageCode(code: string): code is LanguageCode {
    return LanguageCodes.includes(code as LanguageCode);
}

function findNestedTranslateKeyValue(obj: NestedObject, keys: string[]): string | undefined {
    const key = keys.shift();
    if (obj && key !== undefined && Object.prototype.hasOwnProperty.call(obj, key)) {
        if (typeof obj[key] === 'string') {
            return obj[key] as string;
        } else if (typeof obj[key] === 'object') {
            return findNestedTranslateKeyValue(obj[key] as NestedObject, keys);
        }
    }

    return undefined;
}

/**
 * @deprecated Please use the typed function getLanguage instead
 */
export function doesTranslateKeyExistLang(currentLanguageCode: LanguageCode, key: TranslationKey): boolean {
    const parts = key.split('.') as DefaultLanguageKeys[];
    const language = languageFiles[currentLanguageCode];

    const result = findNestedTranslateKeyValue(language, parts);

    return result !== undefined;
}

/**
 * @deprecated Please use the typed function getLanguage if the translation doesn't have variables and the `formatString` function `src/common/utils/formatString.ts` otherwise
 */
export function translateKeyLang(
    currentLanguageCode: LanguageCode,
    key: TranslationKey,
    variable0?: string,
    variable1?: string,
    variable2?: string
): string {
    const parts = key.split('.') as DefaultLanguageKeys[];
    const language = languageFiles[currentLanguageCode];

    let result = findNestedTranslateKeyValue(language, parts);
    if (result === undefined) {
        return '*** Translate error: cannot find key "' + key + '" for ' + currentLanguageCode + ' ***';
    }
    if (variable0 !== undefined) {
        result = result.replace(/\{0\}/g, variable0);
    }
    if (variable1 !== undefined) {
        result = result.replace(/\{1\}/g, variable1);
    }
    if (variable2 !== undefined) {
        result = result.replace(/\{2\}/g, variable2);
    }

    return result;
}

export function getLanguage(language: LanguageCode = 'nl') {
    return languageFiles[isValidLanguageCode(language) ? language : 'nl'];
}

/**
 * Retrieves the translation for a nested language key.
 * @param key - The nested language key string to retrieve the translation for (i.e. `"contactsAndTasks.patientTaskList.birthDate"`).
 * @param languageCode - Optional. The language code to use for retrieving the translation. If not provided, the default language code (`"nl"`) will be used.
 * @returns The translation for the specified nested language key.
 * @returns An error message if the translation for the specified key is not found.
 */
export function getNestedTranslation(key: string, languageCode?: LanguageCode) {
    try {
        const [head, ...tail] = key.split('.');

        const headTranslationKeys = getLanguage(languageCode)[head as keyof DefaultLanguage];

        const result = tail.reduce((acc, nestedKey) => acc[nestedKey as keyof typeof acc], headTranslationKeys);

        if (typeof result === 'string') {
            return result;
        }

        throw new Error();
    } catch (e) {
        return `*** Translate error: cannot find key "${key}" for "${languageCode}" ***`;
    }
}
